/**
 * 浏览器端事件聚合 tracker
 *
 * 解决:N 个用户行为 → 1 个 HTTP 请求,而非 N 个
 * 机制:
 *   1. 内存队列,达 MAX_BATCH 或 FLUSH_INTERVAL 触发 flush
 *   2. 终态监听:pagehide + visibilitychange[hidden] 立即 flush
 *   3. 优先 navigator.sendBeacon(终态可靠),失败回退 fetch + keepalive
 *
 * 设计取舍(基于 browser 2026 现状):
 *   - sendBeacon 用 text/plain Blob 避开 CORS preflight(application/json 在
 *     Chrome 一直有 preflight bug,跨浏览器不可靠)
 *   - 不监听 unload / beforeunload(破坏 bfcache,移动 Safari 不触发)
 *   - fire-and-forget:失败丢弃(公益站 5-10% 丢失可接受,无持久化需求)
 *   - 不引依赖,~50 行实现全套
 *
 * 性能:10 个事件从 10 次 fetch → 1 次 sendBeacon,省 90%+ HTTP 头/TCP 握手
 */
const ENDPOINT = '/api/event/batch'
const MAX_BATCH = 20           // 单次最多 20 事件
const FLUSH_INTERVAL = 3000    // 或每 3 秒
let queue: unknown[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

export function track(body: unknown): Promise<{ ok: true }> {
  queue.push(body)
  if (queue.length >= MAX_BATCH) flush()
  else if (!flushTimer) flushTimer = setTimeout(flush, FLUSH_INTERVAL)
  return Promise.resolve({ ok: true } as const)
}

function flush(): void {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null }
  if (queue.length === 0) return
  const events = queue
  queue = []

  const body = new Blob([JSON.stringify({ events })], { type: 'text/plain' })

  // 优先 sendBeacon(终态可靠:页面卸载后仍投递)
  if (typeof navigator !== 'undefined' && navigator.sendBeacon?.(ENDPOINT, body)) return

  // 回退:fetch + keepalive(浏览器没 sendBeacon 时用,行为类似但不保证终态)
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => { /* best-effort,失败丢弃 */ })
}

// 终态 flush 触发:pagehide(移动 Safari + bfcache 友好) + visibilitychange[hidden](切后台)
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
}
